/**
 * @deprecated Use `LineChart`.However it's still being used. Do not delete.
 */

// Third-party
import React, { Component } from 'react';
import moment from 'moment';
import Highcharts, { SeriesOptionsType } from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import classNames from 'classnames';
import deepmerge from 'deepmerge';

// Components
import { withErrorBoundary } from '@/views/components/errors';
import { DateQuickRangeOneWay, DateRangeSelect } from '@/views/components/inputs';
import { DisclaimerTooltip } from '@/views/components/tooltips';

// Utils
import dom from '@/utils/dom';
import { formatNumber, FormattedMessage } from '@/utils/i18n';
import { capitalizeFirst } from '@/utils/strings';

// Types
import Nullable from '@/types/nullable';
import Locale from '@/types/locale';
import Disclaimer from '@/types/disclaimer';
import GrowthChartOptions from '@/types/growth-chart-options';
import { PERIODS_VALUE_CI, PERIODS_VALUE_INC, TIME_PERIODS } from '@/utils/constants';
import { getMaxEndDate } from '@/utils/dates';
import PERIOD_END_DATA_OPTIONS from '@/types/period-end-data-options';
import { periodToMonths, periodToYear } from '@/utils/time';
import { monthsToPeriodFromValues } from '@/utils/charts';
import { LineChartDatum, LineChartOptions } from '../../LineChart';

type DefaultProps = {
  animate: boolean;
  height: Nullable<number>;
  showDatePickers: boolean;
  onChangeDates: (startDate: Nullable<Date>, endDate: Date, isYTD?: boolean) => void;
  startDate: Nullable<Date>;
  endDate: Date;
  showDateRange: boolean;
  yAxisOpposite: boolean;
  readOnly?: boolean;
};

type Props = {
  title?: string;
  dark?: boolean;
  data: LineChartDatum[];
  locale?: Locale;
  chartPicker?: React.ReactNode;
  chartOptions?: LineChartOptions;
  chartDisclaimer?: Disclaimer;
  yearBasedValues?: boolean;
  emptyStateText?: string;
  values: TIME_PERIODS[];
  monthEndDataOption?: PERIOD_END_DATA_OPTIONS;
} & DefaultProps;

type State = {
  chartOptions: LineChartOptions;
  dateQuickRange: Nullable<TIME_PERIODS>;
};

class LineChart extends Component<Props, State> {
  static defaultProps: DefaultProps = {
    animate: true,
    height: null,
    showDatePickers: true,
    onChangeDates: () => {},
    startDate: null,
    endDate: new Date(),
    showDateRange: true,
    yAxisOpposite: false,
  };

  componentDidUpdate(nextProps: Props) {
    if (this.props.startDate !== nextProps.startDate || this.props.endDate !== nextProps.endDate) {
      setTimeout(() => {
        dom.fireResizeEvent();
      }, 0);
    }
  }

  setOptions = <K extends keyof GrowthChartOptions>(options: Pick<GrowthChartOptions, K>) => {
    this.setState(prevState => ({ ...prevState, ...options }));
  };

  onDateRangeChange = (startDate: Nullable<Date>, endDate: Date, isYTD?: boolean) => {
    this.setState({ dateQuickRange: null });
    this.props.onChangeDates(startDate, endDate, isYTD);
  };

  onQuickDateChange = (dateQuickRange: TIME_PERIODS) => {
    const { yearBasedValues, monthEndDataOption } = this.props;

    this.setState({ dateQuickRange });

    const dateRange = yearBasedValues ? periodToYear(dateQuickRange) : periodToMonths(dateQuickRange);
    const unit = yearBasedValues ? 'year' : 'month';

    const localDateRange = dateRange === PERIODS_VALUE_CI || dateRange === PERIODS_VALUE_INC ? null : dateRange;

    // TODO this is a workaround for now, if we ever add months to the DateQuickRange we'll need to rethink this
    const isYTD = !yearBasedValues && dateRange !== PERIODS_VALUE_CI && dateRange !== PERIODS_VALUE_INC && dateRange < 12;

    const endDate = monthEndDataOption ? getMaxEndDate(this.props.monthEndDataOption) : moment().toDate();
    const startDate = localDateRange != null ? moment(endDate).startOf(unit).subtract(localDateRange, unit).toDate() : null;

    this.props.onChangeDates(startDate, endDate, isYTD);
  };

  getDefaultChartOptions = (): LineChartOptions => {
    const marker: Highcharts.PlotAreaMarkerOptions = {
      symbol: 'circle',
      radius: 0,
      states: {
        hover: {
          lineWidth: 3,
          radius: 5,
        },
        inactive: {
          opacity: 0,
        },
      },
    };
    return {
      series: [] as Highcharts.SeriesLineOptions[],
      rangeSelector: { enabled: false },
      scrollbar: { enabled: false },
      credits: { enabled: false },
      legend: { enabled: false },
      navigator: { enabled: false },
      chart: {
        height: this.props.height,
        spacingLeft: 0,
        spacingRight: 0,
        panning: false,
      },
      tooltip: {
        useHTML: true,
        pointFormat:
          '<span style="color:{point.color};}">\u25CF</span>&nbsp;<span data-private data-dd-privacy="mask">{series.name}</span>: <b style="color:#A4A7B7;">{point.y}</b><br/>',
        backgroundColor: '#000000',
        borderColor: '#000000',
        borderRadius: 6,
        split: true,
        shadow: false,
        style: {
          color: '#ffffff',
        },
      },
      title: {
        text: undefined,
      },
      xAxis: {
        crosshair: {
          className: 'x-crosshair',
          width: 92,
        },
        minTickInterval: 2735696401, // 1 month in milliseconds
        tickWidth: 0,
        lineWidth: 0,
        labels: {
          formatter() {
            return capitalizeFirst(moment(new Date(this.value)).format('MMM YYYY'));
          },
        },
      },
      yAxis: {
        title: {
          text: null,
        },
        opposite: this.props.yAxisOpposite,
        labels: {
          formatter() {
            return formatNumber(this.value, 1);
          },
        },
      },
      plotOptions: {
        area: { marker },
        line: { marker },
        series: {
          animation: this.props.animate,
        },
      },
    };
  };

  handleRangeStartDate = (startDate: Nullable<Date>, series: SeriesOptionsType[]) => {
    if (startDate) return startDate;
    if (!series || !Object.values(series).some(serie => serie?.data?.length > 0)) return null;
    const filteredSeries = series
      .map(serie => serie.data[0][0])
      .filter((item, pos, self) => self.indexOf(item) === pos)
      .sort((a, b) => a - b);
    return filteredSeries?.length ? new Date(filteredSeries[0]) : null;
  };

  render() {
    const { dark, title, startDate, endDate, data, showDatePickers, showDateRange, chartDisclaimer, emptyStateText, chartPicker, locale, readOnly, values } =
      this.props;

    const dateRange = startDate ? moment(endDate).diff(startDate, 'months') : null;
    const dateQuickRange = monthsToPeriodFromValues(dateRange, undefined, values);

    const chartHasData = data.length > 0;

    const highchartsOptions = {
      ...deepmerge(this.getDefaultChartOptions(), this.props.chartOptions || {}),
      series: data,
    };

    const displayToolbar = !!title || showDateRange || showDatePickers || !!chartDisclaimer;

    return (
      <div className={classNames('area-chart', { dark })}>
        {title && (
          <div className="title print">
            <span>{title}</span>
          </div>
        )}
        {displayToolbar && (
          <div className="chart-toolbar-container">
            {chartPicker}
            <div className="toolbar d-flex justify-content-end align-items-center">
              {showDateRange && (
                <DateQuickRangeOneWay
                  className="toolbar-option no-print"
                  selected={dateQuickRange}
                  onChange={this.onQuickDateChange}
                  dark={dark}
                  darkerBackground={dark}
                  values={values}
                />
              )}

              {showDatePickers && (
                <DateRangeSelect
                  className="toolbar-option print"
                  startDate={this.handleRangeStartDate(startDate, highchartsOptions.series)}
                  endDate={endDate}
                  onChange={this.onDateRangeChange}
                  maxDate={getMaxEndDate(this.props.monthEndDataOption)}
                  dark={dark}
                  locale={locale}
                  readOnly={readOnly}
                />
              )}

              {chartDisclaimer && <DisclaimerTooltip className="toolbar-disclaimer" disclaimer={chartDisclaimer} />}
            </div>
          </div>
        )}

        <div className="graph-container">
          {chartHasData && <HighchartsReact highcharts={Highcharts} options={highchartsOptions} constructorType="stockChart" />}

          {!chartHasData && (
            <div className={classNames('empty-chart flex-fill', { 'is-light': !dark })}>
              {emptyStateText !== undefined && <span>{emptyStateText}</span>}

              {emptyStateText === undefined && <FormattedMessage id="Charts.EmptyPeriod" />}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default withErrorBoundary(LineChart);
